import { isChildNode } from 'module/helpers/is-child-node';
import { DateField } from 'module/types/filter-field/field/filterable-field';
import { ChevronDown } from '@carbon/icons-react';
import { Dropdown } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { Trigger } from './components';
import { format } from './format';
import { isBasic } from './helpers';
import { renderOverlay } from './overlay';
import {
  DateFieldFilter,
  DateFieldFilterOperationType,
  DateFieldFilterOperationTypes,
  isDefault,
  isOptional,
} from '../../../../../../types/filter-field/filter/date';

interface Props {
  filter: DateFieldFilter;
  onChange: (filter: DateFieldFilter) => void;
  getFilterOperationTypeOptions?: (field: DateField) => DateFieldFilter[];
  getOperationOptionRenderer?: (
    type: DateFieldFilterOperationType,
    filter?: DateFieldFilter,
  ) => null | React.JSX.Element;
}

const defaultOptions = (field: DateField): DateFieldFilter[] =>
  DateFieldFilterOperationTypes.filter((x) => isDefault(x))
    .filter((x) => !field.metaData?.required || !isOptional(x))
    .map(
      (type) =>
        ({
          field,
          type,
        }) as any as DateFieldFilter,
    );

export const DateCriteria: FC<React.PropsWithChildren<Props>> = ({
  filter,
  onChange,
  getFilterOperationTypeOptions = defaultOptions,
  getOperationOptionRenderer,
}) => {
  const [visible, setVisible] = useState(false);
  const [advanced, setAdvanced] = useState(!isBasic(filter.type));
  const items = getFilterOperationTypeOptions(filter.field);
  const isChild = isChildNode([
    'date-criteria',
    'ant-dropdown',
    'ant-picker-dropdown',
  ]);
  const close = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (target != null && !isChild(target)) {
        setVisible(false);
      }
    },
    [setVisible, isChild],
  );

  useEffect(() => {
    if (!visible) {
      return () => {};
    }
    document.addEventListener('click', close);

    return () => {
      document.removeEventListener('click', close);
    };
  }, [close, visible]);

  useEffect(() => {
    if (!visible) {
      setAdvanced(!isBasic(filter.type));
    }
  }, [filter.type, setAdvanced, visible]);

  useEffect(() => {
    const hasBasicFilterOptions =
      items.filter((x) => isBasic(x.type)).length > 0;

    if (!hasBasicFilterOptions) {
      setAdvanced(true);
    }
  }, [visible, items]);

  const onAdvancedClick = useCallback(() => setAdvanced(true), [setAdvanced]);

  return (
    <Dropdown
      trigger={['click']}
      visible={visible}
      overlay={renderOverlay(
        filter,
        onChange,
        setVisible,
        items,
        onAdvancedClick,
        advanced,
        getOperationOptionRenderer,
      )}
    >
      <Trigger
        data-cy="criteria.op_trigger"
        className="date-criteria"
        onClick={() => setVisible(!visible)}
      >
        <span>{format(filter, getOperationOptionRenderer)}</span>
        <ChevronDown />
      </Trigger>
    </Dropdown>
  );
};
